<template>
	<div :class="{ pageRoot: true }">
		<div class="heading">Power Status</div>
		<div class="bodySection">
			<p style="font-weight: bold;">
				{{ipmiPowerStatus}}
			</p>
			<button class="refreshButton" @click="GetIpmiPowerStatus" :disabled="ipmiPowerLoading">
				<vsvg sprite="refresh" class="refreshIcon" /> Refresh
			</button>
		</div>
		<div class="heading">Change Your Machine's Power State</div>
		<div class="bodySection">
			<div v-for="ps in ipmiPowerOptions" :key="ps.name">
				<label class="radioLabel">
					<input type="radio" :value="ps.name" v-model="selectedPowerState"> {{ps.description}}
				</label>
			</div>
			<p></p>
			<slide-button :key="'slideBtn_powerstate_' + powerStateSlideBtnCounter"
						  :auto-width="false"
						  :circle="true"
						  :disabled="ipmiPowerLoading"
						  :noanimate="true"
						  :width="300"
						  :height="60"
						  text="slide to set"
						  success-text="setting"
						  @completed="onPowerStateCommanded()"
						  :requireReleaseToComplete="true" />
		</div>
		<div class="heading">IPMI (Remote KVM) Firewall Status</div>
		<div class="bodySection">
			<div class="ipmiConnectionInfoMessage" v-dompurify-html="ipmiConnectionInfo.html"></div>
			<template v-if="ipmiConnectionInfo.details">
				<table class="ipmiDetails">
					<tbody>
						<tr v-for="d in ipmiConnectionInfo.details" :key="d.key">
							<td>{{d.key}}</td>
							<td class="copy" @click="copy(d.value)">{{d.value}}</td>
						</tr>
					</tbody>
				</table>
				<p v-if="ipmiConnectionInfo.firstsetup"><i>The source IP address in the status description above has been known to be incorrect upon first setup.  If you click "Refresh" below, it should be fixed.</i></p>
				<p v-else></p>
			</template>
			<button class="refreshButton" @click="GetIpmiConnectionInformation" :disabled="ipmiConnectionInfo.loading">
				<vsvg sprite="refresh" class="refreshIcon" /> Refresh
			</button>
		</div>
		<div class="heading">Setup IPMI Firewall</div>
		<div class="bodySection">
			<div class="yourIpSection">
				<button class="loadIp" @click="LoadPublicIP" title="Puts your public IP into the box" :disabled="yourIpLoading">
					<vsvg sprite="refresh" class="refreshIcon" />
				</button>
				<label>
					<b>Your IP Address</b><br />
					<input type="text" class="yourIp" v-model="yourIp" />
				</label>
			</div>
			<p>The above IP address will be granted temporary access to your InterServer workstation's IPMI (remote KVM) interface.</p>
			<div style="margin-top: 1em;">
			</div>
			<div style="margin-top: 1em;">
				<slide-button :key="'slideBtn_powerstate_' + ipmiConnectionSlideBtnCounter"
							  :auto-width="false"
							  :circle="true"
							  :disabled="ipmiConnectionInfo.loading"
							  :noanimate="true"
							  :width="300"
							  :height="60"
							  text="setup IPMI"
							  success-text="setting"
							  @completed="onIpmiConnectionCommanded()"
							  :requireReleaseToComplete="true" />
			</div>
		</div>
	</div>
</template>

<script>
	import svg1 from 'appRoot/images/sprite/refresh.svg';
	import SlideButton from 'appRoot/vues/common/controls/SlideButton.vue';
	import { GetIpmiPowerStatus, SetIpmiPowerStatus, GetIpmiConnectionInformation, SetupIpmiConnectionInformation } from 'appRoot/api/UserIpmiData';
	import { CopyToClipboard } from 'appRoot/scripts/Util';

	export default {
		components: { SlideButton },
		props:
		{
		},
		data()
		{
			return {
				appPath: appContext.appPath,
				ipmiPowerStatus: "",
				ipmiPowerOptions: [],
				ipmiPowerLoading: false,
				ipmiConnectionInfo: { html: "<b>This is a test</b>", details: [], loading: false, firstsetup: false },
				powerStateSlideBtnCounter: 0,
				ipmiConnectionSlideBtnCounter: 0,
				selectedPowerState: "on",
				yourIp: "...",
				yourIpLoading: false,
			};
		},
		async created()
		{
			this.GetIpmiPowerStatus();
			this.GetIpmiConnectionInformation();
			this.LoadPublicIP();
		},
		computed:
		{
		},
		methods:
		{
			async GetIpmiPowerStatus()
			{
				if (this.ipmiPowerLoading)
					return;
				this.ipmiPowerStatus = "Loading...";
				this.ipmiPowerLoading = true;
				try
				{
					let response = await GetIpmiPowerStatus();
					this.ipmiPowerStatus = response.status;
					this.ipmiPowerOptions = response.ipmiPowerStatusOptions;
				}
				catch (ex)
				{
					this.ipmiPowerStatus = ex.message;
				}
				finally
				{
					this.ipmiPowerLoading = false;
				}
			},
			async onPowerStateCommanded()
			{
				if (this.ipmiPowerLoading)
					return;
				this.ipmiPowerStatus = "Loading...";
				this.ipmiPowerLoading = true;
				try
				{
					let response = await SetIpmiPowerStatus(this.selectedPowerState);
					this.ipmiPowerStatus = response.status;
					this.ipmiPowerOptions = response.ipmiPowerStatusOptions;
				}
				catch (ex)
				{
					this.ipmiPowerStatus = ex.message;
				}
				finally
				{
					this.powerStateSlideBtnCounter++;
					this.ipmiPowerLoading = false;
				}
			},
			async GetIpmiConnectionInformation()
			{
				this.ipmiConnectionInfo.details = [];
				this.ipmiConnectionInfo.loading = true;
				try
				{
					let response = await GetIpmiConnectionInformation();
					this.handleIpmiConnectionInfo(response.info);
				}
				catch (ex)
				{
					this.addDetail("ERROR", ex.message);
				}
				finally
				{
					this.ipmiConnectionInfo.loading = false;
				}
			},
			async onIpmiConnectionCommanded()
			{
				this.ipmiConnectionInfo.details = [];
				this.ipmiConnectionInfo.loading = true;
				try
				{
					let response = await SetupIpmiConnectionInformation(this.yourIp);
					this.handleIpmiConnectionInfo(response.info);
					this.ipmiConnectionInfo.firstsetup = true;
				}
				catch (ex)
				{
					this.addDetail("ERROR", ex.message);
				}
				finally
				{
					this.ipmiConnectionSlideBtnCounter++;
					this.ipmiConnectionInfo.loading = false;
				}
			},
			handleIpmiConnectionInfo(info)
			{
				this.ipmiConnectionInfo.html = info.text;
				this.addDetail("Allowed Client IP Address", info.allowed_ip);
				this.addDetail("IPMI IP Address", info.public_ip);
				this.addDetail("IPMI Username", info.client_username);
				this.addDetail("IPMI Password", info.client_password);
			},
			addDetail(key, value)
			{
				if (key && value)
					this.ipmiConnectionInfo.details.push({ key, value });
			},
			async LoadPublicIP()
			{
				if (this.yourIpLoading)
					return;
				if (location.hostname == "127.0.0.1" || location.hostname == "localhost")
				{
					this.yourIp = "127.0.0.1";
					return;
				}
				this.yourIpLoading = true;
				try
				{
					let response = await fetch("https://online.statref.com/testip/justip");
					let text = await response.text();
					this.yourIp = text;
				}
				catch (ex)
				{
					toaster.Error(ex);
				}
				finally
				{
					this.yourIpLoading = false;
				}
			},
			copy(text)
			{
				CopyToClipboard(text);
				toaster.Success("Copied to clipboard", text, 1500);
			}
		},
		watch:
		{
		}
	}
</script>

<style scoped>
	.pageRoot
	{
		margin: 8px;
	}

	.heading
	{
		font-size: 20px;
		border-bottom: 1px solid #000000;
		margin-bottom: 10px;
		margin-top: 30px;
		max-width: 400px;
	}

		.heading:first-child
		{
			margin-top: 0px;
		}

	.refreshButton
	{
		height: 44px;
		padding: 0px;
		padding-right: 10px;
		display: flex;
		align-items: center;
	}

		.refreshButton .refreshIcon
		{
			width: 32px;
			height: 32px;
			padding-left: 5px;
			padding-right: 3px;
		}

	.bodySection
	{
		margin: 0px 20px;
	}

	.radioLabel
	{
		display: flex;
		align-items: baseline;
		height: 36px;
		line-height: 36px;
		max-width: 300px;
	}

		.radioLabel:hover
		{
			background-color: rgba(0,0,0,0.05);
		}

		.radioLabel input
		{
			margin-right: 8px;
		}

	.ipmiConnectionInfoMessage
	{
		display: inline-block;
		border: 3px dotted #FF8800;
		padding: 1em;
		margin-bottom: 1em;
	}

		.ipmiConnectionInfoMessage :deep(h4:first-child)
		{
			margin-top: 0px;
		}

		.ipmiConnectionInfoMessage :deep(h4:last-child)
		{
			margin-bottom: 0px;
		}

	.ipmiDetails
	{
		border-collapse: collapse;
	}

		.ipmiDetails td
		{
			border: 1px solid black;
			padding: 2px 4px;
		}

			.ipmiDetails td:first-child
			{
				font-weight: bold;
				text-align: right;
			}

			.ipmiDetails td:last-child
			{
				user-select: all;
				cursor: pointer;
			}

				.ipmiDetails td:last-child:hover
				{
					background-color: rgba(0,0,0,0.05);
				}

	.yourIpSection
	{
		display: flex;
		align-items: flex-end;
	}

	.loadIp
	{
		width: 44px;
		height: 44px;
		padding: 0px;
		margin-right: 1em;
	}

		.loadIp .refreshIcon
		{
			width: 32px;
			height: 32px;
		}

	.yourIp
	{
		width: 300px;
		font-size: 12pt;
	}

	@media (min-width: 600px)
	{
		.additionalEmailsTextArea
		{
			min-width: 400px;
		}
	}
</style>